import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import HeaderMenu from "../component/Navbar/headermenu";
import CustomFooter from "../component/Footer/customFooter";
import icaiChairmanPhoto from "../assets/icaichairmain.JPG";
import {
  BadgePercent,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  FileText,
  Gift,
  History,
  LayoutGrid,
  MailCheck,
  MessageSquare,
  PlayCircle,
  Repeat,
  ShieldCheck,
  Tag,
  UserPlus,
  Wallet,
} from "lucide-react";

// Dedicated client for CA Elite endpoints (enroll/verify/plan) — these live
// on the admin API, separate from the app's main apiClient baseURL.
const caApiClient = axios.create({
  baseURL: "https://admin.simplifyte.com/api/",
  headers: { Accept: "application/json" },
});

const steps = [
  {
    number: "01",
    icon: UserPlus,
    title: "Share your details",
    description:
      "Enter your name, mobile number and ICAI Membership Registration Number.",
  },
  {
    number: "02",
    icon: MailCheck,
    title: "Confirm your contact details",
    description:
      "Verify your email or mobile number using a one-time code.",
  },
  {
    number: "03",
    icon: Gift,
    title: "Unlock CA Elite",
    description:
      "Once verified, your CA Elite member pricing is activated and shared with you.",
  },
];

const features = [
  {
    icon: BadgePercent,
    title: "CA Elite member pricing",
    description:
      "A preferential rate created exclusively for verified Chartered Accountants.",
    featured: true,
  },
  {
    icon: Wallet,
    title: "No reduced-feature version",
    description:
      "Access the complete CA Elite feature set for managing tasks, teams, clients, documents and deadlines.",
  },
  {
    icon: Tag,
    title: "Your member rate stays protected",
    description:
      "Continue with CA Elite pricing on renewals while your subscription and eligibility remain valid.",
  },
  {
    icon: Clock,
    title: "Verify once",
    description:
      "Complete your MRN verification once and access CA Elite without repeating the process at every renewal.",
  },
  {
    icon: LayoutGrid,
    title: "Built for structured professional work",
    description:
      "Bring deadlines, team reviews, client communication, documents and audit trails into one organised workspace.",
  },
];

const faqs = [
  {
    question: "Who is eligible for the CA Elite Plan?",
    answer:
      "Any Chartered Accountant holding a valid ICAI Membership Registration Number may apply for CA Elite, subject to successful verification.",
  },
  {
    question: "Is CA Elite limited to practicing CAs or CA firms?",
    answer:
      "No. CA Elite is available to eligible Chartered Accountants with a valid MRN. A Certificate of Practice or firm registration is not required unless specifically stated in the plan terms.",
  },
  {
    question: "Why do you ask for my MRN?",
    answer:
      "Your MRN helps us confirm that you are eligible for CA Elite and protects the plan from unauthorised access. It is used only for verification and is not displayed publicly.",
  },
  {
    question: "What does the CA Elite Plan include?",
    answer:
      "CA Elite includes all the Elite Features, with 5 users to begin with that can be increased as and when required. There is no reduced-feature version unless clearly stated in the plan details.",
  },
  {
    question: "How is CA Elite pricing activated?",
    answer:
      "Once verified, you will receive a personal CA Elite access code by email and mobile. The code is linked to your verified details and cannot be transferred.",
  },
  {
    question: "Will my CA Elite member rate continue on renewal?",
    answer:
      "Your CA Elite member rate will continue on eligible renewals while your subscription remains active and the applicable plan terms are met.",
  },
  {
    question: "Can I share my CA Elite access with another person?",
    answer:
      "No. CA Elite access is issued against a verified MRN and is non-transferable.",
  },
  {
    question: "Can one MRN be used for multiple workspaces?",
    answer:
      "One verified MRN may be linked to one primary CA Elite workspace unless additional access is approved by Simplifyte.",
  },
  {
    question: "Is CA Elite an ICAI scheme or endorsement?",
    answer:
      "CA Elite is an independent initiative offered by Simplifyte for verified Chartered Accountants. It should not be understood as an ICAI scheme, partnership or endorsement unless expressly stated otherwise.",
  },
  {
    question: "How is my verification data protected?",
    answer:
      "Verification information is collected only for eligibility, account security and compliance purposes. It is handled in accordance with Simplifyte's Privacy Policy and Terms of Service.",
    isPrivacyAnswer: true,
  },
];

const capabilities = [
  {
    icon: Repeat,
    title: "Recurring compliance work",
    description: "Organise recurring tasks by client, financial year and period.",
  },
  {
    icon: CheckCircle2,
    title: "Review and approval",
    description: "Build maker-checker accountability into team workflows.",
  },
  {
    icon: Clock,
    title: "Deadline visibility",
    description: "See what is due, delayed and awaiting action.",
  },
  {
    icon: FileText,
    title: "Documents",
    description: "Keep task-related files structured and searchable.",
  },
  {
    icon: MessageSquare,
    title: "Client communication",
    description:
      "Replace scattered WhatsApp and email follow-ups with organised context.",
  },
  {
    icon: History,
    title: "Audit trail",
    description: "Preserve actions, comments, changes and approvals.",
  },
];

const STEP_LABELS = ["Details", "Workspace", "Confirm"];

const getFlagEmoji = (iso2) =>
  iso2
    .toUpperCase()
    .replace(/./g, (char) =>
      String.fromCodePoint(127397 + char.charCodeAt(0))
    );

const RAW_COUNTRY_CODES = [
  { code: "+91", country: "India", iso2: "IN" },
  { code: "+93", country: "Afghanistan", iso2: "AF" },
  { code: "+355", country: "Albania", iso2: "AL" },
  { code: "+213", country: "Algeria", iso2: "DZ" },
  { code: "+1684", country: "American Samoa", iso2: "AS" },
  { code: "+376", country: "Andorra", iso2: "AD" },
  { code: "+244", country: "Angola", iso2: "AO" },
  { code: "+1264", country: "Anguilla", iso2: "AI" },
  { code: "+1268", country: "Antigua and Barbuda", iso2: "AG" },
  { code: "+54", country: "Argentina", iso2: "AR" },
  { code: "+374", country: "Armenia", iso2: "AM" },
  { code: "+297", country: "Aruba", iso2: "AW" },
  { code: "+61", country: "Australia", iso2: "AU" },
  { code: "+43", country: "Austria", iso2: "AT" },
  { code: "+994", country: "Azerbaijan", iso2: "AZ" },
  { code: "+1242", country: "Bahamas", iso2: "BS" },
  { code: "+973", country: "Bahrain", iso2: "BH" },
  { code: "+880", country: "Bangladesh", iso2: "BD" },
  { code: "+1246", country: "Barbados", iso2: "BB" },
  { code: "+375", country: "Belarus", iso2: "BY" },
  { code: "+32", country: "Belgium", iso2: "BE" },
  { code: "+501", country: "Belize", iso2: "BZ" },
  { code: "+229", country: "Benin", iso2: "BJ" },
  { code: "+1441", country: "Bermuda", iso2: "BM" },
  { code: "+975", country: "Bhutan", iso2: "BT" },
  { code: "+591", country: "Bolivia", iso2: "BO" },
  { code: "+387", country: "Bosnia and Herzegovina", iso2: "BA" },
  { code: "+267", country: "Botswana", iso2: "BW" },
  { code: "+55", country: "Brazil", iso2: "BR" },
  { code: "+673", country: "Brunei", iso2: "BN" },
  { code: "+359", country: "Bulgaria", iso2: "BG" },
  { code: "+226", country: "Burkina Faso", iso2: "BF" },
  { code: "+257", country: "Burundi", iso2: "BI" },
  { code: "+855", country: "Cambodia", iso2: "KH" },
  { code: "+237", country: "Cameroon", iso2: "CM" },
  { code: "+1", country: "Canada", iso2: "CA" },
  { code: "+238", country: "Cape Verde", iso2: "CV" },
  { code: "+1345", country: "Cayman Islands", iso2: "KY" },
  { code: "+236", country: "Central African Republic", iso2: "CF" },
  { code: "+235", country: "Chad", iso2: "TD" },
  { code: "+56", country: "Chile", iso2: "CL" },
  { code: "+86", country: "China", iso2: "CN" },
  { code: "+57", country: "Colombia", iso2: "CO" },
  { code: "+269", country: "Comoros", iso2: "KM" },
  { code: "+242", country: "Congo", iso2: "CG" },
  { code: "+243", country: "Congo (DRC)", iso2: "CD" },
  { code: "+682", country: "Cook Islands", iso2: "CK" },
  { code: "+506", country: "Costa Rica", iso2: "CR" },
  { code: "+385", country: "Croatia", iso2: "HR" },
  { code: "+53", country: "Cuba", iso2: "CU" },
  { code: "+357", country: "Cyprus", iso2: "CY" },
  { code: "+420", country: "Czech Republic", iso2: "CZ" },
  { code: "+45", country: "Denmark", iso2: "DK" },
  { code: "+253", country: "Djibouti", iso2: "DJ" },
  { code: "+1767", country: "Dominica", iso2: "DM" },
  { code: "+1809", country: "Dominican Republic", iso2: "DO" },
  { code: "+593", country: "Ecuador", iso2: "EC" },
  { code: "+20", country: "Egypt", iso2: "EG" },
  { code: "+503", country: "El Salvador", iso2: "SV" },
  { code: "+240", country: "Equatorial Guinea", iso2: "GQ" },
  { code: "+291", country: "Eritrea", iso2: "ER" },
  { code: "+372", country: "Estonia", iso2: "EE" },
  { code: "+268", country: "Eswatini", iso2: "SZ" },
  { code: "+251", country: "Ethiopia", iso2: "ET" },
  { code: "+679", country: "Fiji", iso2: "FJ" },
  { code: "+358", country: "Finland", iso2: "FI" },
  { code: "+33", country: "France", iso2: "FR" },
  { code: "+241", country: "Gabon", iso2: "GA" },
  { code: "+220", country: "Gambia", iso2: "GM" },
  { code: "+995", country: "Georgia", iso2: "GE" },
  { code: "+49", country: "Germany", iso2: "DE" },
  { code: "+233", country: "Ghana", iso2: "GH" },
  { code: "+350", country: "Gibraltar", iso2: "GI" },
  { code: "+30", country: "Greece", iso2: "GR" },
  { code: "+299", country: "Greenland", iso2: "GL" },
  { code: "+1473", country: "Grenada", iso2: "GD" },
  { code: "+1671", country: "Guam", iso2: "GU" },
  { code: "+502", country: "Guatemala", iso2: "GT" },
  { code: "+224", country: "Guinea", iso2: "GN" },
  { code: "+245", country: "Guinea-Bissau", iso2: "GW" },
  { code: "+592", country: "Guyana", iso2: "GY" },
  { code: "+509", country: "Haiti", iso2: "HT" },
  { code: "+504", country: "Honduras", iso2: "HN" },
  { code: "+852", country: "Hong Kong", iso2: "HK" },
  { code: "+36", country: "Hungary", iso2: "HU" },
  { code: "+354", country: "Iceland", iso2: "IS" },
  { code: "+62", country: "Indonesia", iso2: "ID" },
  { code: "+98", country: "Iran", iso2: "IR" },
  { code: "+964", country: "Iraq", iso2: "IQ" },
  { code: "+353", country: "Ireland", iso2: "IE" },
  { code: "+972", country: "Israel", iso2: "IL" },
  { code: "+39", country: "Italy", iso2: "IT" },
  { code: "+1876", country: "Jamaica", iso2: "JM" },
  { code: "+81", country: "Japan", iso2: "JP" },
  { code: "+962", country: "Jordan", iso2: "JO" },
  { code: "+7", country: "Kazakhstan", iso2: "KZ" },
  { code: "+254", country: "Kenya", iso2: "KE" },
  { code: "+686", country: "Kiribati", iso2: "KI" },
  { code: "+965", country: "Kuwait", iso2: "KW" },
  { code: "+996", country: "Kyrgyzstan", iso2: "KG" },
  { code: "+856", country: "Laos", iso2: "LA" },
  { code: "+371", country: "Latvia", iso2: "LV" },
  { code: "+961", country: "Lebanon", iso2: "LB" },
  { code: "+266", country: "Lesotho", iso2: "LS" },
  { code: "+231", country: "Liberia", iso2: "LR" },
  { code: "+218", country: "Libya", iso2: "LY" },
  { code: "+423", country: "Liechtenstein", iso2: "LI" },
  { code: "+370", country: "Lithuania", iso2: "LT" },
  { code: "+352", country: "Luxembourg", iso2: "LU" },
  { code: "+853", country: "Macau", iso2: "MO" },
  { code: "+261", country: "Madagascar", iso2: "MG" },
  { code: "+265", country: "Malawi", iso2: "MW" },
  { code: "+60", country: "Malaysia", iso2: "MY" },
  { code: "+960", country: "Maldives", iso2: "MV" },
  { code: "+223", country: "Mali", iso2: "ML" },
  { code: "+356", country: "Malta", iso2: "MT" },
  { code: "+692", country: "Marshall Islands", iso2: "MH" },
  { code: "+222", country: "Mauritania", iso2: "MR" },
  { code: "+230", country: "Mauritius", iso2: "MU" },
  { code: "+52", country: "Mexico", iso2: "MX" },
  { code: "+691", country: "Micronesia", iso2: "FM" },
  { code: "+373", country: "Moldova", iso2: "MD" },
  { code: "+377", country: "Monaco", iso2: "MC" },
  { code: "+976", country: "Mongolia", iso2: "MN" },
  { code: "+382", country: "Montenegro", iso2: "ME" },
  { code: "+1664", country: "Montserrat", iso2: "MS" },
  { code: "+212", country: "Morocco", iso2: "MA" },
  { code: "+258", country: "Mozambique", iso2: "MZ" },
  { code: "+95", country: "Myanmar", iso2: "MM" },
  { code: "+264", country: "Namibia", iso2: "NA" },
  { code: "+674", country: "Nauru", iso2: "NR" },
  { code: "+977", country: "Nepal", iso2: "NP" },
  { code: "+31", country: "Netherlands", iso2: "NL" },
  { code: "+687", country: "New Caledonia", iso2: "NC" },
  { code: "+64", country: "New Zealand", iso2: "NZ" },
  { code: "+505", country: "Nicaragua", iso2: "NI" },
  { code: "+227", country: "Niger", iso2: "NE" },
  { code: "+234", country: "Nigeria", iso2: "NG" },
  { code: "+683", country: "Niue", iso2: "NU" },
  { code: "+850", country: "North Korea", iso2: "KP" },
  { code: "+389", country: "North Macedonia", iso2: "MK" },
  { code: "+47", country: "Norway", iso2: "NO" },
  { code: "+968", country: "Oman", iso2: "OM" },
  { code: "+92", country: "Pakistan", iso2: "PK" },
  { code: "+680", country: "Palau", iso2: "PW" },
  { code: "+970", country: "Palestine", iso2: "PS" },
  { code: "+507", country: "Panama", iso2: "PA" },
  { code: "+675", country: "Papua New Guinea", iso2: "PG" },
  { code: "+595", country: "Paraguay", iso2: "PY" },
  { code: "+51", country: "Peru", iso2: "PE" },
  { code: "+63", country: "Philippines", iso2: "PH" },
  { code: "+48", country: "Poland", iso2: "PL" },
  { code: "+351", country: "Portugal", iso2: "PT" },
  { code: "+1787", country: "Puerto Rico", iso2: "PR" },
  { code: "+974", country: "Qatar", iso2: "QA" },
  { code: "+40", country: "Romania", iso2: "RO" },
  { code: "+7", country: "Russia", iso2: "RU" },
  { code: "+250", country: "Rwanda", iso2: "RW" },
  { code: "+1869", country: "Saint Kitts and Nevis", iso2: "KN" },
  { code: "+1758", country: "Saint Lucia", iso2: "LC" },
  { code: "+1784", country: "Saint Vincent and the Grenadines", iso2: "VC" },
  { code: "+685", country: "Samoa", iso2: "WS" },
  { code: "+378", country: "San Marino", iso2: "SM" },
  { code: "+239", country: "Sao Tome and Principe", iso2: "ST" },
  { code: "+966", country: "Saudi Arabia", iso2: "SA" },
  { code: "+221", country: "Senegal", iso2: "SN" },
  { code: "+381", country: "Serbia", iso2: "RS" },
  { code: "+248", country: "Seychelles", iso2: "SC" },
  { code: "+232", country: "Sierra Leone", iso2: "SL" },
  { code: "+65", country: "Singapore", iso2: "SG" },
  { code: "+421", country: "Slovakia", iso2: "SK" },
  { code: "+386", country: "Slovenia", iso2: "SI" },
  { code: "+677", country: "Solomon Islands", iso2: "SB" },
  { code: "+252", country: "Somalia", iso2: "SO" },
  { code: "+27", country: "South Africa", iso2: "ZA" },
  { code: "+82", country: "South Korea", iso2: "KR" },
  { code: "+211", country: "South Sudan", iso2: "SS" },
  { code: "+34", country: "Spain", iso2: "ES" },
  { code: "+94", country: "Sri Lanka", iso2: "LK" },
  { code: "+249", country: "Sudan", iso2: "SD" },
  { code: "+597", country: "Suriname", iso2: "SR" },
  { code: "+46", country: "Sweden", iso2: "SE" },
  { code: "+41", country: "Switzerland", iso2: "CH" },
  { code: "+963", country: "Syria", iso2: "SY" },
  { code: "+886", country: "Taiwan", iso2: "TW" },
  { code: "+992", country: "Tajikistan", iso2: "TJ" },
  { code: "+255", country: "Tanzania", iso2: "TZ" },
  { code: "+66", country: "Thailand", iso2: "TH" },
  { code: "+670", country: "Timor-Leste", iso2: "TL" },
  { code: "+228", country: "Togo", iso2: "TG" },
  { code: "+676", country: "Tonga", iso2: "TO" },
  { code: "+1868", country: "Trinidad and Tobago", iso2: "TT" },
  { code: "+216", country: "Tunisia", iso2: "TN" },
  { code: "+90", country: "Turkey", iso2: "TR" },
  { code: "+993", country: "Turkmenistan", iso2: "TM" },
  { code: "+688", country: "Tuvalu", iso2: "TV" },
  { code: "+256", country: "Uganda", iso2: "UG" },
  { code: "+380", country: "Ukraine", iso2: "UA" },
  { code: "+971", country: "United Arab Emirates", iso2: "AE" },
  { code: "+44", country: "United Kingdom", iso2: "GB" },
  { code: "+1", country: "United States", iso2: "US" },
  { code: "+598", country: "Uruguay", iso2: "UY" },
  { code: "+998", country: "Uzbekistan", iso2: "UZ" },
  { code: "+678", country: "Vanuatu", iso2: "VU" },
  { code: "+379", country: "Vatican City", iso2: "VA" },
  { code: "+58", country: "Venezuela", iso2: "VE" },
  { code: "+84", country: "Vietnam", iso2: "VN" },
  { code: "+967", country: "Yemen", iso2: "YE" },
  { code: "+260", country: "Zambia", iso2: "ZM" },
  { code: "+263", country: "Zimbabwe", iso2: "ZW" },
];

const COUNTRY_CODES = RAW_COUNTRY_CODES.map((c) => ({
  ...c,
  flag: getFlagEmoji(c.iso2),
  label: `${getFlagEmoji(c.iso2)} ${c.code}`,
}));

const initialFormData = {
  enrolmentDate: "",
  name: "",
  country: "India",
  countryCode: "+91",
  mobile: "",
  mrn: "",
  email: "",
  otp: "",
};

const CACommunity = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [caToken, setCaToken] = useState("");
  const [tokenCopied, setTokenCopied] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState("");
  const [planPrice, setPlanPrice] = useState("");
  const [planUserLimit, setPlanUserLimit] = useState("");
  const [planBillingCycle, setPlanBillingCycle] = useState("year");
  const otpInputRefs = useRef([]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (step === 3) {
      otpInputRefs.current[0]?.focus();
    }
  }, [step]);

  useEffect(() => {
    let isMounted = true;

    caApiClient
      .get("/ca-elite-plan")
      .then((response) => {
        if (!isMounted) return;
        const plan = response.data?.data || {};
        const yearlyPrice = parseFloat(plan.yearly_price);
        if (!Number.isNaN(yearlyPrice) && yearlyPrice > 0) {
          setPlanPrice(yearlyPrice.toLocaleString("en-IN"));
        }
        if (plan.included_users) setPlanUserLimit(plan.included_users);
        setPlanBillingCycle("year");
      })
      .catch((err) => {
        console.log("CA Elite plan fetch failed:", err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (e) => {
    const countryName = e.target.value;
    const match = COUNTRY_CODES.find((c) => c.country === countryName);
    setFormData((prev) => ({
      ...prev,
      country: countryName,
      countryCode: match ? match.code : prev.countryCode,
    }));
  };

  // OTP boxes are a presentational layer over formData.otp — the existing
  // verify handler keeps reading/writing formData.otp as a single string.
  const handleOtpBoxChange = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, "").slice(-1);
    const otpChars = formData.otp.padEnd(6, " ").split("");
    otpChars[index] = digit || " ";
    const nextOtp = otpChars.join("").replace(/ /g, "");
    setFormData((prev) => ({ ...prev, otp: nextOtp }));

    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpBoxKeyDown = (index, e) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    setFormData((prev) => ({ ...prev, otp: pasted }));
    const lastIndex = Math.min(pasted.length, 6) - 1;
    otpInputRefs.current[lastIndex]?.focus();
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (
      !formData.enrolmentDate ||
      !formData.name ||
      !formData.mobile ||
      !formData.mrn
    ) {
      setError(
        "Please fill in your enrolment date, name, mobile number, and MRN."
      );
      return;
    }
    const digitsOnly = formData.mobile.replace(/\D/g, "");
    if (formData.countryCode === "+91" && !/^\d{10}$/.test(digitsOnly)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (digitsOnly.length < 6 || digitsOnly.length > 14) {
      setError("Please enter a valid mobile number.");
      return;
    }
    if (!/^\d{6}$/.test(formData.mrn)) {
      setError("Please enter a valid 6-digit MRN number.");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError("Please enter your workspace email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setResendMessage("");
    setIsLoading(true);

    try {
      await caApiClient.post("/ca-community/enroll", {
        enrolmentDate: formData.enrolmentDate,
        name: formData.name,
        mobile: `${formData.countryCode}${formData.mobile}`,
        mrn: formData.mrn,
        email: formData.email,
      });
    } catch (err) {
      console.log("CA enroll API call failed:", err);
    } finally {
      setIsLoading(false);
    }

    setResendCooldown(10);
    setFormData((prev) => ({ ...prev, otp: "" }));
    setStep(3);
  };

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(formData.otp)) {
      setError("Please enter the 6-digit OTP.");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      const response = await caApiClient.post("/ca-community/verify", {
        email: formData.email,
        otp: formData.otp,
      });
      setSuccessMessage(
        response.data?.message ||
          "OTP verified. Your CA token has been sent to your email."
      );
      setCaToken(response.data?.ca_token || "");
    } catch (err) {
      console.log("CA verify API call failed:", err);
      setError(
        err.response?.data?.message ||
          "Could not verify OTP. Please try again."
      );
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setIsSubmitted(true);
  };

  const handleBack = () => {
    setError("");
    setResendMessage("");
    setResendCooldown(0);
    setStep((prev) => Math.max(1, prev - 1));
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setStep(1);
    setError("");
    setSuccessMessage("");
    setCaToken("");
    setTokenCopied(false);
    setResendCooldown(0);
    setResendMessage("");
    setFormData(initialFormData);
  };

  const handleContinueToCAElite = () => {
    window.location.href = "https://app.simplifyte.com/settings/upgradePlan";
  };

  const handleCopyToken = async () => {
    if (!caToken) return;

    let copied = false;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(caToken);
        copied = true;
      } catch (err) {
        console.log("Clipboard API copy failed:", err);
      }
    }

    if (!copied) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = caToken;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        copied = document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (err) {
        console.log("Fallback copy failed:", err);
      }
    }

    if (copied) {
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || isResending) return;
    setError("");
    setResendMessage("");
    setIsResending(true);
    setFormData((prev) => ({ ...prev, otp: "" }));

    try {
      await caApiClient.post("/ca-community/enroll", {
        enrolmentDate: formData.enrolmentDate,
        name: formData.name,
        mobile: `${formData.countryCode}${formData.mobile}`,
        mrn: formData.mrn,
        email: formData.email,
      });
      setResendMessage("A new OTP has been sent to your email.");
    } catch (err) {
      console.log("CA resend OTP API call failed:", err);
      setResendMessage("A new OTP has been sent to your email.");
    } finally {
      setIsResending(false);
      setResendCooldown(10);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <style dangerouslySetInnerHTML={{ __html: `
        .ca-input {
          width: 100% !important;
          height: 46px !important;
          padding: 0 16px !important;
          border: 1px solid #dbe7ec !important;
          border-radius: 10px !important;
          font-size: 15px !important;
          outline: none !important;
          transition: border-color 0.2s ease !important;
          color: #073653 !important;
          background-color: #ffffff !important;
        }
        .ca-input::placeholder { color: #a6b5bc !important; }
        .ca-input:focus { border-color: #08a9c4 !important; box-shadow: 0 0 0 3px rgba(8,169,196,0.13) !important; }

        /* Date input matched to the same 46px / 1px / 10px tokens as text inputs */
        .ca-input[type="date"] {
          -webkit-appearance: none !important;
          appearance: none !important;
        }
        .ca-input[type="date"]::-webkit-calendar-picker-indicator {
          position: absolute;
          right: 12px;
          width: 18px;
          height: 18px;
          cursor: pointer;
          opacity: 0.55;
        }
        .ca-date-wrap { position: relative !important; }

        .ca-phone-group {
          display: grid !important;
          grid-template-columns: 104px 1fr !important;
          gap: 8px !important;
        }
        .ca-country-select {
          height: 46px !important;
          width: 104px !important;
          padding: 0 26px 0 10px !important;
          border: 1px solid #dbe7ec !important;
          border-radius: 10px !important;
          font-size: 14px !important;
          font-weight: 600 !important;
          outline: none !important;
          color: #073653 !important;
          background-color: #f8fafb !important;
          cursor: pointer !important;
          transition: border-color 0.2s ease !important;
          text-overflow: ellipsis !important;
          -webkit-appearance: none !important;
          appearance: none !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23899ca6' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: right 10px center !important;
        }
        .ca-country-select:focus {
          border-color: #08a9c4 !important;
          box-shadow: 0 0 0 3px rgba(8,169,196,0.13) !important;
        }
        .ca-phone-input {
          min-width: 0 !important;
        }

        .ca-otp-box {
          width: 100% !important;
          height: 58px !important;
          border: 1px solid #dbe7ec !important;
          border-radius: 10px !important;
          text-align: center !important;
          font-size: 22px !important;
          font-weight: 600 !important;
          color: #073653 !important;
          outline: none !important;
          transition: border-color 0.2s ease !important;
        }
        .ca-otp-box:focus { border-color: #08a9c4 !important; box-shadow: 0 0 0 3px rgba(8,169,196,0.13) !important; }
        .ca-otp-box.is-filled { border-color: #073f60 !important; }

        .ca-btn-primary {
          height: 50px !important;
          background-color: #073f60 !important;
          color: #ffffff !important;
          font-weight: 500 !important;
          font-size: 15.5px !important;
          border: none !important;
          border-radius: 10px !important;
          cursor: pointer !important;
          transition: background-color 0.2s ease, transform 0.1s ease !important;
        }
        .ca-btn-primary:hover { background-color: #0a4d75 !important; }
        .ca-btn-primary:active { transform: scale(0.994); }
        .ca-btn-secondary {
          height: 50px !important;
          width: auto !important;
          padding: 0 20px !important;
          background-color: #ffffff !important;
          color: #073653 !important;
          font-weight: 500 !important;
          font-size: 15.5px !important;
          border: 1px solid #dbe7ec !important;
          border-radius: 10px !important;
          cursor: pointer !important;
          white-space: nowrap !important;
          transition: background-color 0.2s ease, border-color 0.2s ease !important;
        }
        .ca-btn-secondary:hover { background-color: #f8fafb !important; border-color: #c7d5db !important; }
        .ca-btn-secondary.ca-btn-block { width: 100% !important; }

        .ca-card {
          min-height: 500px !important;
          display: flex !important;
          flex-direction: column !important;
        }
        .ca-card-body { flex: 1 1 auto !important; }
        .ca-card-actions { margin-top: auto !important; }
        .ca-btn-primary, .ca-btn-secondary { white-space: nowrap !important; }

        .ca-connector-fill {
          transition: width 0.35s ease !important;
        }

        @media (max-width: 1039px) {
          .ca-card { min-height: 0 !important; }
        }
        @media (max-width: 519px) {
          .ca-hero-h1 { font-size: 34px !important; }
          .ca-card { padding: 24px 20px !important; }
          .ca-mrn-date-grid { grid-template-columns: 1fr !important; }
          .ca-otp-grid { gap: 7px !important; }
          .ca-otp-box { height: 50px !important; }
        }
      `}} />

      <HeaderMenu />

      <main className="flex-grow">
        {/* =========================
            HERO SECTION
        ========================== */}
        <section
          id="join-form"
          className="relative flex min-h-[calc(100vh-72px)] items-center overflow-hidden bg-[#f8fcfe]"
        >
          <div className="mx-auto w-full max-w-[1240px] px-6 py-14 lg:px-8 lg:py-16">
            <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-16">
              {/* Hero Content */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <div className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-[#d7eaf2] bg-white px-4 py-2 text-sm font-semibold tracking-wide text-[#075579] shadow-sm">
                  <span className="h-2 w-2 rounded-full bg-[#00b8d4]" />
                  Reserved for verified Chartered Accountants
                </div>

                <h1 className="ca-hero-h1 max-w-[11ch] text-[56px] font-bold leading-[1.06] tracking-[-0.028em] text-[#073653]">
                  Introducing{" "}<br />
                  <span className="text-[#08a9c4]">CA Elite</span> —
                  Simplifyte for Chartered Accountants.
                </h1>

                <div className="mt-4 h-[3px] w-16 rounded-full bg-[#08a9c4]" />

                <p className="mt-6 max-w-[520px] text-lg leading-7 text-[#5b7280]">
                  Verify your MRN to unlock the CA Elite Plan—member-only
                  pricing and complete Simplifyte capabilities for you and
                  your team.
                </p>

                <div className="mt-7">
                  <a
                    href="#whats-included"
                    className="inline-block border-b border-[#08a9c4]/40 text-base font-semibold text-[#073653] transition hover:border-[#08a9c4]"
                  >
                    See What's Included
                  </a>
                </div>
              </motion.div>

              {/* Registration Card */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.15 }}
                className="ca-card relative w-full max-w-[440px] rounded-[18px] border border-[#e4edf1] bg-white shadow-[0_24px_70px_rgba(10,65,90,0.12)]"
                style={{ padding: "30px 32px 26px" }}
              >
                {isSubmitted ? (
                  <div className="ca-card-body flex flex-col text-center">
                    <div className="flex-1">
                    <div className="relative mx-auto mb-5 flex h-16 w-16 items-center justify-center">
                      <motion.span
                        initial={{ opacity: 0.6, scale: 0.8 }}
                        animate={{ opacity: 0, scale: 1.6 }}
                        transition={{ duration: 1.4, ease: "easeOut", repeat: Infinity }}
                        className="absolute inset-0 rounded-full bg-[#08a9c4]/25"
                      />
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }}
                        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#08a9c4] to-[#073f60] text-white shadow-[0_8px_20px_rgba(8,169,196,0.35)]"
                      >
                        <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
                          <motion.path
                            d="M8 16.5L13.5 22L24 10"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.35, ease: "easeOut" }}
                          />
                        </svg>
                      </motion.div>
                    </div>
                    <h3 className="text-xl font-bold text-[#073653]">
                      EMAIL CONFIRMED
                    </h3>
                    <p className="mx-auto mt-2 max-w-[360px] text-sm leading-6 text-[#5b7280]">
                      Thanks,{" "}
                      <span className="font-semibold text-[#073653]">
                        {formData.name}
                      </span>
                      . Your access code has also been sent to your email.
                    </p>

                    {caToken && (
                      <div className="mt-5 text-left">
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#899ca6]">
                          CA ELITE ACCESS CODE
                        </label>
                        <div
                          className={`flex items-center gap-2 rounded-lg border bg-[#f8fafb] px-3.5 transition-colors ${
                            tokenCopied
                              ? "border-[#1a9c5c]"
                              : "border-[#dbe7ec]"
                          }`}
                          style={{ height: 36 }}
                        >
                          <code className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-sm text-[#073653]">
                            {caToken}
                          </code>
                          <button
                            type="button"
                            onClick={handleCopyToken}
                            className={`flex flex-shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition ${
                              tokenCopied
                                ? "border-[#1a9c5c] bg-white text-[#1a9c5c]"
                                : "border-[#dbe7ec] bg-white text-[#073f60] hover:bg-[#eef4f7]"
                            }`}
                            style={{ height: 28 }}
                          >
                            {tokenCopied ? (
                              <>
                                <Check size={13} />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy size={13} />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                        <p className="mt-2 text-xs leading-5 text-[#899ca6]">
                          Use this code in Settings → Upgrade Plan to unlock CA Elite.
                        </p>
                      </div>
                    )}
                    </div>

                    <div className="ca-card-actions grid gap-3">
                      <button
                        onClick={handleContinueToCAElite}
                        className="ca-btn-primary w-full text-sm"
                      >
                        Continue to CA Elite
                      </button>
                      <button
                        onClick={resetForm}
                        className="ca-btn-secondary ca-btn-block text-sm"
                        type="button"
                      >
                        Use it later
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="ca-card-body flex flex-col">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-[0.16em] text-[#08a9c4]">
                          Unlock CA Elite
                        </div>
                        <div className="mt-1 text-lg font-bold text-[#073653]">
                          Step {step} of 3
                        </div>
                      </div>

                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#073f60] text-white">
                        <BadgePercent size={15} />
                      </div>
                    </div>

                    <div className="mb-5 mt-5 flex items-center">
                      {[1, 2, 3].map((s, idx) => (
                        <React.Fragment key={s}>
                          <div className="flex flex-col items-center">
                            <div
                              className={`flex items-center justify-center rounded-full text-[11px] font-bold border-[1.5px] transition-colors duration-200 ${
                                step > s
                                  ? "bg-[#073f60] border-[#073f60] text-white"
                                  : step === s
                                  ? "border-[#073f60] text-[#073f60] bg-white"
                                  : "border-[#e2ebef] text-[#a6b5bc] bg-[#f3f6f7]"
                              }`}
                              style={{ width: 26, height: 26 }}
                            >
                              {step > s ? <Check size={13} /> : s}
                            </div>
                            <span
                              className={`mt-1.5 font-semibold ${
                                step >= s ? "text-[#073f60]" : "text-[#a6b5bc]"
                              }`}
                              style={{ fontSize: 11.5 }}
                            >
                              {STEP_LABELS[idx]}
                            </span>
                          </div>
                          {idx < 2 && (
                            <div
                              className="mx-2 mb-4 flex-1 rounded-full bg-[#e2ebef]"
                              style={{ height: 2 }}
                            >
                              <div
                                className="ca-connector-fill h-full rounded-full bg-[#073f60]"
                                style={{ width: step > s ? "100%" : "0%" }}
                              />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    {error && (
                      <div className="mb-4 rounded-lg border border-red-100 bg-red-50 p-3 text-sm font-medium text-red-600">
                        {error}
                      </div>
                    )}

                    {step === 1 && (
                      <form onSubmit={handleStep1Submit} className="flex flex-1 flex-col space-y-3">
                        <div>
                          <label className="mb-1.5 block text-sm font-semibold text-[#073653]">
                            Full name
                          </label>
                          <input
                            type="text"
                            name="name"
                            placeholder="Mahesh Kumar"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="ca-input"
                          />
                        </div>

                        <div>
                          <label className="mb-1.5 block text-sm font-semibold text-[#073653]">
                            Mobile number
                          </label>
                          <div className="ca-phone-group">
                            <select
                              name="country"
                              value={formData.country}
                              onChange={handleCountryChange}
                              className="ca-country-select"
                            >
                              {COUNTRY_CODES.map((c) => (
                                <option key={c.country} value={c.country}>
                                  {c.label}
                                </option>
                              ))}
                            </select>
                            <input
                              type="tel"
                              name="mobile"
                              placeholder="98765 43210"
                              value={formData.mobile}
                              onChange={handleChange}
                              required
                              className="ca-input ca-phone-input"
                            />
                          </div>
                        </div>

                        <div className="ca-mrn-date-grid grid grid-cols-2 gap-[14px]">
                          <div>
                            <label className="mb-1.5 block text-sm font-semibold text-[#073653]">
                              MRN number
                            </label>
                            <input
                              type="text"
                              name="mrn"
                              placeholder="Enter your 6-digit MRN"
                              value={formData.mrn}
                              onChange={(e) =>
                                handleChange({
                                  target: {
                                    name: "mrn",
                                    value: e.target.value
                                      .replace(/\D/g, "")
                                      .slice(0, 6),
                                  },
                                })
                              }
                              inputMode="numeric"
                              maxLength={6}
                              required
                              className="ca-input"
                            />
                          </div>

                          <div>
                            <label className="mb-1.5 block text-sm font-semibold text-[#073653]">
                              Enrolment date
                            </label>
                            <div className="ca-date-wrap">
                              <input
                                type="date"
                                name="enrolmentDate"
                                value={formData.enrolmentDate}
                                onChange={handleChange}
                                required
                                className="ca-input"
                              />
                            </div>
                          </div>
                        </div>
                        <p className="-mt-1.5 flex items-center gap-1.5 text-xs text-[#899ca6]">
                          <ShieldCheck size={13} className="flex-shrink-0" />
                          Used only to verify you're a practicing CA.
                        </p>

                        <div className="ca-card-actions">
                          <button
                            type="submit"
                            className="ca-btn-primary flex w-full items-center justify-center gap-2 text-sm"
                          >
                            Continue
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      </form>
                    )}

                    {step === 2 && (
                      <form onSubmit={handleStep2Submit} className="flex flex-1 flex-col space-y-4">
                        <p className="text-sm leading-6 text-[#5b7280]">
                          Choose the email that owns—or will be used to
                          create—your Simplifyte workspace.
                        </p>
                        <div>
                          <label className="mb-1.5 block text-sm font-semibold text-[#073653]">
                            Workspace owner email
                          </label>
                          <input
                            type="email"
                            name="email"
                            placeholder="Enter the workspace owner's email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            autoFocus
                            className="ca-input"
                          />
                          <p className="mt-1.5 text-xs leading-5 text-[#899ca6]">
                            CA Elite access will be linked to this email—use
                            the email that owns (or will create) your
                            Simplifyte workspace. You can invite your team
                            later.
                          </p>
                        </div>

                        <div className="ca-card-actions grid grid-cols-1 items-center gap-3 pt-1 [@media(min-width:400px)]:grid-cols-[auto_1fr]">
                          <button
                            type="button"
                            onClick={handleBack}
                            disabled={isLoading}
                            className="ca-btn-secondary order-2 text-sm [@media(min-width:400px)]:order-1"
                          >
                            Back to CA details
                          </button>
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="ca-btn-primary order-1 flex items-center justify-center gap-2 text-sm disabled:opacity-60 [@media(min-width:400px)]:order-2"
                          >
                            {isLoading ? "Sending..." : "Send Verification Code"}
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      </form>
                    )}

                    {step === 3 && (
                      <form onSubmit={handleStep3Submit} className="flex flex-1 flex-col space-y-4">
                        <div>
                          <h4 className="text-base font-bold text-[#073653]">
                            Confirm your workspace email
                          </h4>
                          <p className="mt-1 text-sm leading-6 text-[#5b7280]">
                            Enter the 6-digit code sent to{" "}
                            <span className="font-semibold text-[#073653]">
                              {formData.email}
                            </span>{" "}
                            to link your CA Elite access.
                          </p>
                        </div>

                        <div>
                          <label className="mb-1.5 block text-sm font-semibold text-[#073653]">
                            6-digit verification code
                          </label>
                          <div className="ca-otp-grid grid grid-cols-6 gap-[10px]">
                            {Array.from({ length: 6 }).map((_, idx) => (
                              <input
                                key={idx}
                                ref={(el) => (otpInputRefs.current[idx] = el)}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={formData.otp[idx] || ""}
                                onChange={(e) =>
                                  handleOtpBoxChange(idx, e.target.value)
                                }
                                onKeyDown={(e) => handleOtpBoxKeyDown(idx, e)}
                                onPaste={handleOtpPaste}
                                className={`ca-otp-box${
                                  formData.otp[idx] ? " is-filled" : ""
                                }`}
                              />
                            ))}
                          </div>
                        </div>

                        <div className="text-center" style={{ fontSize: 13.5 }}>
                          {resendMessage ? (
                            <span className="font-medium text-[#137333]">
                              {resendMessage}
                            </span>
                          ) : (
                            <span className="text-[#899ca6]">
                              Didn't receive it?
                            </span>
                          )}{" "}
                          <button
                            type="button"
                            onClick={handleResendOtp}
                            disabled={resendCooldown > 0 || isResending}
                            className="text-[#a6b5bc] disabled:cursor-not-allowed enabled:font-medium enabled:text-[#073f60] enabled:hover:underline"
                          >
                            {isResending
                              ? "Resending..."
                              : resendCooldown > 0
                              ? `Resend code in ${resendCooldown}s`
                              : "Resend code"}
                          </button>
                        </div>

                        <div className="ca-card-actions space-y-2 pt-1">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="ca-btn-primary flex w-full items-center justify-center gap-2 text-sm disabled:opacity-60"
                          >
                            {isLoading ? "Verifying..." : "Verify & Unlock"}
                            <ArrowRight size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={handleBack}
                            disabled={isLoading}
                            className="ca-btn-secondary ca-btn-block text-sm"
                          >
                            Use a different email
                          </button>
                        </div>
                      </form>
                    )}

                    {(step === 2 || step === 3) && (
                      <div className="mt-4 flex items-center gap-2 text-xs text-[#9aa9b0]">
                        <ShieldCheck size={13} className="flex-shrink-0" />
                        Secure verification &middot; The code can only be used
                        once.
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </div>
          </div>

        </section>

        {/* =========================
            ICAI CREDIBILITY
        ========================== */}
        <section className="relative overflow-hidden border-y border-[#e5eef2] bg-[#f8fcfe] py-20">
          <div
            className="pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-[#08a9c4]/[0.06] blur-3xl"
            aria-hidden="true"
          />
          <div className="relative mx-auto max-w-[1100px] px-6">
            <div className="grid items-center gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5 }}
              >
                <div className="group relative overflow-hidden rounded-[20px] shadow-[0_24px_60px_rgba(10,65,90,0.12)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_32px_70px_rgba(10,65,90,0.2)]">
                  <img
                    src={icaiChairmanPhoto}
                    alt="The Simplifyte team with CA Charanjot Singh Nanda, Chairman of the ICAI AI Committee and Past President of ICAI, at the Simplifyte booth during the AI Innovation Summit 2026"
                    className="w-full scale-100 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="absolute bottom-0 left-0 right-0 py-3 px-4 text-center  border-white/10">
                    <div className="absolute inset-0 bg-black/55 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <p className="relative text-xs sm:text-sm font-medium text-white/90 tracking-wide">
                      Simplifyte Making an Impact at AIS 2026 | Bharat Mandapam, New Delhi
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="inline-flex items-center gap-2.5 rounded-full border border-[#d7eaf2] bg-white px-4 py-2 text-sm font-semibold tracking-wide text-[#075579] shadow-sm">
                  <ShieldCheck size={16} className="text-[#08a9c4]" />
                  Shaped through conversations with the profession
                </div>

                <h2 className="mt-5 max-w-[18ch] text-3xl font-extrabold leading-[1.15] tracking-[-0.02em] text-[#073653] sm:text-4xl">
                  Built around how Chartered Accountants and their teams
                  actually work.
                </h2>

                <div className="mt-4 h-[3px] w-16 rounded-full bg-[#08a9c4]" />

                <p className="mt-6 max-w-[520px] text-base leading-7 text-[#5b7280]">
                  At the ICAI AI Innovation Summit 2026, we demonstrated how
                  Simplifyte brings recurring work, deadlines, team reviews,
                  documents and approvals into one structured workspace.
                </p>

                <p className="mt-4 max-w-[520px] text-base leading-7 text-[#5b7280]">
                  Conversations with Chartered Accountants continue to help
                  us understand where professional work becomes
                  complicated—and what Simplifyte should make simpler.
                </p>

                <a
                  href="#whats-included"
                  className="mt-7 inline-flex items-center gap-2 text-base font-semibold text-[#08a9c4] transition hover:text-[#073f60]"
                >
                  See How Simplifyte Helps CA Teams
                  <ArrowRight size={18} />
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* =========================
            HOW IT WORKS
        ========================== */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-[1240px] px-6">
            <div className="mx-auto max-w-[680px] text-center">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-[#08a9c4]">
                How access works
              </div>

              <h2 className="mt-4 text-4xl font-extrabold tracking-[-0.03em] text-[#073653] sm:text-5xl">
                Your CA Elite access, in three steps.
              </h2>

              <p className="mt-5 text-lg text-[#5b7280]">
                A quick eligibility check using your MRN—without lengthy
                forms or sales calls.
              </p>
            </div>

            <div className="mt-16 grid gap-6 md:grid-cols-3">
              {steps.map((step, index) => {
                const Icon = step.icon;

                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -4 }}
                    className="rounded-2xl border border-[#e1ebef] bg-white p-8 shadow-[0_8px_30px_rgba(20,70,90,0.04)] transition-shadow duration-300 hover:shadow-lg"
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#073f60] text-white">
                      <Icon size={22} />
                    </div>

                    <div className="mt-7 text-sm font-bold uppercase tracking-[0.18em] text-[#08a9c4]">
                      Step {step.number}
                    </div>

                    <h3 className="mt-2 text-xl font-bold text-[#073653]">
                      {step.title}
                    </h3>

                    <p className="mt-3 text-base leading-7 text-[#5b7280]">
                      {step.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================
            FEATURES
        ========================== */}
        <section id="whats-included" className="bg-[#f5fafc] py-24">
          <div className="mx-auto max-w-[1240px] px-6">
            <div className="mx-auto max-w-[680px] text-center">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-[#08a9c4]">
                CA Elite benefits
              </div>

              <h2 className="mt-4 text-4xl font-extrabold tracking-[-0.03em] text-[#073653] sm:text-5xl">
                More than member pricing. A plan built for professional work.
              </h2>
            </div>

            <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, index) => {
                const Icon = feature.icon;

                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    whileHover={{ y: -4 }}
                    className={`rounded-2xl border p-8 transition-shadow duration-300 hover:shadow-lg ${
                      feature.featured
                        ? "border-[#076083] bg-[#076083] text-white lg:col-span-2"
                        : "border-[#e0ebef] bg-white"
                    }`}
                  >
                    <div
                      className={`flex h-14 w-14 items-center justify-center rounded-full ${
                        feature.featured
                          ? "bg-white/15 text-white"
                          : "bg-[#edf3f5] text-[#073f60]"
                      }`}
                    >
                      <Icon size={22} />
                    </div>

                    <h3 className="mt-7 text-xl font-bold">{feature.title}</h3>

                    <p
                      className={`mt-3 text-base leading-7 ${
                        feature.featured ? "text-white/75" : "text-[#5b7280]"
                      }`}
                    >
                      {feature.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================
            BUILT FOR CA WORK
        ========================== */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-[1240px] px-6">
            <div className="mx-auto max-w-[720px] text-center">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-[#08a9c4]">
                Built for professional practice
              </div>

              <h2 className="mt-4 text-4xl font-extrabold tracking-[-0.03em] text-[#073653] sm:text-5xl">
                From recurring deadlines to final review—keep every
                responsibility visible.
              </h2>
            </div>

            <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {capabilities.map((capability, index) => {
                const Icon = capability.icon;

                return (
                  <motion.div
                    key={capability.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: index * 0.07 }}
                    whileHover={{ y: -4 }}
                    className="rounded-2xl border border-[#e0ebef] bg-white p-8 transition-shadow duration-300 hover:shadow-lg"
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#edf3f5] text-[#073f60]">
                      <Icon size={22} />
                    </div>

                    <h3 className="mt-7 text-xl font-bold text-[#073653]">
                      {capability.title}
                    </h3>

                    <p className="mt-3 text-base leading-7 text-[#5b7280]">
                      {capability.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-12 flex justify-center">
              <a
                href="https://www.youtube.com/watch?v=lOraef1arpk"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5 text-base font-semibold text-[#073f60] hover:underline"
              >
                <PlayCircle
                  size={20}
                  className="text-[#08a9c4] transition-transform group-hover:scale-110"
                />
                Watch the 5-Minute Simplifyte Demo
              </a>
            </div>
          </div>
        </section>

        {/* =========================
            PRICING
        ========================== */}
        <section className="bg-[#f5fafc] py-24">
          <div className="mx-auto max-w-[1240px] px-6">
            <div className="mx-auto max-w-[720px] text-center">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-[#08a9c4]">
                CA Elite pricing
              </div>

              <h2 className="mt-4 text-4xl font-extrabold tracking-[-0.03em] text-[#073653] sm:text-5xl">
                One plan. Reserved member pricing.
              </h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5 }}
              className="mx-auto mt-14 max-w-[440px] rounded-[24px] border-2 border-[#08a9c4] bg-white p-9 shadow-[0_24px_60px_rgba(8,169,196,0.15)]"
            >
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#08a9c4]">
                CA Elite Plan
              </div>
              <p className="mt-1.5 text-sm text-[#5b7280]">
                Reserved for verified Chartered Accountants
              </p>

              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-[#073653]">₹</span>
                <span className="text-5xl font-extrabold tracking-tight text-[#073653]">
                  {planPrice || "[CA Elite price]"}
                </span>
                <span className="text-base font-semibold text-[#5b7280]">
                  /{planBillingCycle}
                </span>
              </div>
              <p className="mt-1.5 text-sm text-[#5b7280]">
                Includes up to {planUserLimit || "[X]"} users
              </p>

              <div className="mt-7 space-y-3 border-t border-[#edf2f4] pt-7">
                {[
                  "Full Elite feature access",
                  "Member rate protected on eligible renewals",
                  "One-time MRN verification",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5">
                    <Check size={16} className="flex-shrink-0 text-[#08a9c4]" />
                    <span className="text-sm text-[#073653]">{item}</span>
                  </div>
                ))}
              </div>

              <a
                href="#join-form"
                className="ca-btn-primary mt-8 flex w-full items-center justify-center gap-2 py-3.5 text-base"
              >
                Unlock CA Elite
                <ArrowRight size={16} />
              </a>

              <p className="mt-4 text-center text-xs text-[#899ca6]">
                CA Elite pricing reflects an approximate 80% professional
                concession compared with the standard Elite plan.
              </p>
            </motion.div>
          </div>
        </section>

        {/* =========================
            WHY THIS EXISTS
        ========================== */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-[1240px] px-6">
            <div className="mx-auto max-w-[680px] text-center">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-[#08a9c4]">
                Why CA Elite exists
              </div>

              <h2 className="mt-4 text-4xl font-extrabold tracking-[-0.03em] text-[#073653] sm:text-5xl">
                Because professional responsibility deserves professional
                systems.
              </h2>

              <p className="mt-5 text-lg leading-8 text-[#5b7280]">
                Chartered Accountants manage recurring deadlines, sensitive
                information, team accountability and client expectations
                every day. CA Elite is our way of giving verified members
                direct access to a structured, professional-grade workspace
                at a reserved member rate.
              </p>
            </div>
          </div>
        </section>

        {/* =========================
            FAQ
        ========================== */}
        <section className="bg-[#f5fafc] py-24">
          <div className="mx-auto max-w-[820px] px-6">
            <div className="text-center">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-[#08a9c4]">
                Common questions
              </div>

              <h2 className="mt-4 text-4xl font-extrabold tracking-[-0.03em] text-[#073653] sm:text-5xl">
                Everything to know about CA Elite.
              </h2>

              <p className="mt-5 text-lg text-[#5b7280]">
                Still have a question? Speak with the Simplifyte team.{" "}
                <a
                  href="mailto:sales@simplifyte.com"
                  className="font-semibold text-[#073f60] underline"
                >
                  Contact CA Elite Support
                </a>
                .
              </p>
            </div>

            <div className="mt-12 space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index;

                return (
                  <div
                    key={faq.question}
                    className="overflow-hidden rounded-xl border border-[#e1ebef] bg-white"
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between px-7 py-5 text-left text-lg font-semibold text-[#073653]"
                    >
                      <span>{faq.question}</span>

                      <ChevronDown
                        size={20}
                        className={`shrink-0 text-[#073f60] transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="overflow-hidden border-t border-[#edf2f4] px-7 text-base leading-7 text-[#5b7280]"
                      >
                        <div className="py-5">
                          {faq.answer}
                          {faq.isPrivacyAnswer && (
                            <>
                              {" "}
                              Read our{" "}
                              <a
                                href="/privacy-policy"
                                className="font-semibold text-[#073f60] underline"
                              >
                                Privacy Policy
                              </a>{" "}
                              and{" "}
                              <a
                                href="/terms"
                                className="font-semibold text-[#073f60] underline"
                              >
                                Terms of Service
                              </a>{" "}
                              for details.
                            </>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================
            FINAL CTA
        ========================== */}
        <section className="bg-white px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-[1100px] overflow-hidden rounded-[28px] bg-[#063e5e] px-8 py-14 text-white sm:px-12 lg:px-16"
          >
            <div className="grid items-center gap-12 lg:grid-cols-[1fr_auto]">
              <div>
                <div className="mb-5 inline-flex rounded-full border border-white/20 px-4 py-1.5 text-sm font-semibold text-white/70">
                  Verified Chartered Accountants only
                </div>

                <h2 className="max-w-[600px] text-4xl font-extrabold leading-tight tracking-[-0.03em] sm:text-5xl">
                  Verify your membership. Unlock CA Elite.
                </h2>

                <p className="mt-5 max-w-[560px] text-lg leading-7 text-white/70">
                  Complete a quick MRN verification to access the CA Elite
                  Plan and its reserved member pricing.
                </p>
              </div>

              

              <div className="flex min-w-[260px] flex-col gap-3">
                <a
                  href="#join-form"
                  className="flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-base font-bold text-[#073f60] transition hover:bg-slate-100"
                >
                  Unlock CA Elite
                  <ArrowRight size={17} />
                </a>

                <a
                  href="/"
                  className="rounded-full border border-white/25 px-7 py-4 text-center text-base font-semibold text-white transition hover:bg-white/10"
                >
                  Explore Simplifyte
                </a>

                <div className="text-center text-sm text-white/45">
                  Secure verification &middot; MRN required &middot; Access
                  is non-transferable
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      <CustomFooter />
    </div>
  );
};

export default CACommunity;
